import { ApiError, NetworkError, unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { pickLocalized, type Locale } from '@mrigl/shared';
import { Alert, Button, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useId, useState, type ChangeEvent, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import { useI18n, type I18n } from '../i18n.js';

type StoreDetails = Schemas['StoreDetails'];
type MediaAsset = Schemas['MediaAsset'];

/** Types the server accepts; anything else is sent as octet-stream and identified there. */
const UPLOAD_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const ACCEPT = [...UPLOAD_TYPES].join(',');
const FILE_ERRORS = new Set([
  'payload-too-large',
  'unsupported-media-type',
  'invalid-image',
  'service-unavailable',
]);

/** The request body is the file itself (no multipart): the server sniffs its real type. */
export function uploadImage(storeId: string, file: File): Promise<MediaAsset> {
  return unwrap(
    api.POST('/admin-api/v1/stores/{storeId}/media', {
      params: { path: { storeId }, query: { filename: file.name } },
      body: file as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: {
        'content-type': UPLOAD_TYPES.has(file.type) ? file.type : 'application/octet-stream',
      },
    }),
  );
}

function fileError(t: I18n['t'], name: string, failure: unknown): string {
  if (failure instanceof ApiError && FILE_ERRORS.has(failure.code)) {
    return t(`admin.media.error.${failure.code}` as MessageKey, { name });
  }
  if (failure instanceof NetworkError || failure instanceof ApiError) {
    return t('admin.media.error.file', { name, message: errorMessage(t, failure) });
  }
  return t('admin.media.error.file', { name, message: t('admin.error.generic') });
}

/** Store media library: upload, browse, describe (alt text) and delete images. */
export function MediaPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const inputId = useId();
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [items, setItems] = useState<MediaAsset[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (after?: string) => {
      setBusy(true);
      try {
        const page = await unwrap(
          api.GET('/admin-api/v1/stores/{storeId}/media', {
            params: {
              path: { storeId },
              query: { limit: 48, ...(after === undefined ? {} : { cursor: after }) },
            },
          }),
        );
        setItems((current) =>
          after === undefined ? page.items : [...(current ?? []), ...page.items],
        );
        setCursor(page.nextCursor);
      } catch (failure) {
        setError(
          failure instanceof ApiError && failure.code === 'service-unavailable'
            ? t('admin.media.error.service-unavailable')
            : errorMessage(t, failure),
        );
        setItems((current) => current ?? []);
      } finally {
        setBusy(false);
      }
    },
    [storeId, t],
  );

  useEffect(() => {
    void unwrap(api.GET('/admin-api/v1/stores/{storeId}', { params: { path: { storeId } } }))
      .then(setStore)
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
    void load();
  }, [storeId, load, t]);

  async function onFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.currentTarget.files ?? [])];
    event.currentTarget.value = '';
    if (files.length === 0) return;
    setNotice(null);
    setFileErrors([]);
    const added: MediaAsset[] = [];
    const failures: string[] = [];
    for (const [index, file] of files.entries()) {
      setProgress({ done: index + 1, total: files.length });
      try {
        added.push(await uploadImage(storeId, file));
      } catch (failure) {
        failures.push(fileError(t, file.name, failure));
      }
    }
    setProgress(null);
    setItems((current) => [...added.reverse(), ...(current ?? [])]);
    setFileErrors(failures);
    if (added.length > 0) setNotice(t('admin.media.uploaded', { count: added.length }));
  }

  if (store === null || items === null) {
    return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  }
  const canManage = store.permissions.includes('media.manage');
  const storeName = pickLocalized(store.name, locale, store.defaultLocale) ?? store.slug;

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{storeName}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.media.title')}</h1>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {canManage ? (
        <div className="mrigl-card mrigl-stack">
          <label className="admin-upload" htmlFor={inputId}>
            <span className="mrigl-button">{t('admin.media.upload')}</span>
            <input
              id={inputId}
              className="admin-upload__input"
              type="file"
              accept={ACCEPT}
              multiple
              disabled={progress !== null}
              aria-describedby={`${inputId}-hint`}
              onChange={(event) => void onFiles(event)}
            />
          </label>
          <p id={`${inputId}-hint`} className="mrigl-muted">
            {t('admin.media.uploadHint')}
          </p>
          <div role="status">
            {progress === null ? null : <p>{t('admin.media.uploading', progress)}</p>}
            {notice === null ? null : <Alert tone="success">{notice}</Alert>}
          </div>
          {fileErrors.length === 0 ? null : (
            <Alert tone="error">
              <ul className="mrigl-list">
                {fileErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="mrigl-muted">{t('admin.media.empty')}</p>
      ) : (
        <ul className="admin-media-grid">
          {items.map((item) => (
            <MediaCard
              key={item.mediaId}
              storeId={storeId}
              item={item}
              locales={store.enabledLocales}
              canManage={canManage}
              onChange={(updated) => {
                setItems((current) =>
                  (current ?? []).map((existing) =>
                    existing.mediaId === updated.mediaId ? updated : existing,
                  ),
                );
              }}
              onDeleted={() => {
                setItems((current) =>
                  (current ?? []).filter((existing) => existing.mediaId !== item.mediaId),
                );
                setNotice(t('admin.media.deleted'));
              }}
            />
          ))}
        </ul>
      )}
      {cursor === null ? null : (
        <Button variant="secondary" busy={busy} onClick={() => void load(cursor)}>
          {t('admin.loadMore')}
        </Button>
      )}
    </div>
  );
}

function MediaCard({
  storeId,
  item,
  locales,
  canManage,
  onChange,
  onDeleted,
}: {
  readonly storeId: string;
  readonly item: MediaAsset;
  readonly locales: readonly Locale[];
  readonly canManage: boolean;
  readonly onChange: (item: MediaAsset) => void;
  readonly onDeleted: () => void;
}) {
  const { t, locale } = useI18n();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [alt, setAlt] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const thumbnail = item.variants[0] ?? item;
  const description = pickLocalized(item.alt, locale, locales[0] ?? locale) ?? '';

  function startEditing() {
    setAlt(Object.fromEntries(locales.map((code) => [code, item.alt[code] ?? ''])));
    setError(null);
    setEditing(true);
  }

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onChange(
        await unwrap(
          api.PATCH('/admin-api/v1/stores/{storeId}/media/{mediaId}', {
            params: { path: { storeId, mediaId: item.mediaId } },
            body: { alt },
          }),
        ),
      );
      setEditing(false);
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.DELETE('/admin-api/v1/stores/{storeId}/media/{mediaId}', {
          params: { path: { storeId, mediaId: item.mediaId } },
        }),
      );
      onDeleted();
    } catch (failure) {
      setError(errorMessage(t, failure));
      setBusy(false);
    }
  }

  return (
    <li className="mrigl-card admin-media-card">
      <figure className="admin-media-card__figure">
        <img
          src={thumbnail.url}
          width={thumbnail.width}
          height={thumbnail.height}
          alt={description}
          loading="lazy"
          decoding="async"
        />
        <figcaption>
          <span className="admin-media-card__name">{item.filename}</span>
          <span className="mrigl-muted mrigl-ltr">
            {t('admin.media.dimensions', { width: item.width, height: item.height })}
          </span>
        </figcaption>
      </figure>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {canManage && editing ? (
        <form
          className="mrigl-stack"
          aria-label={t('admin.media.editFor', { name: item.filename })}
          onSubmit={(event) => void save(event)}
        >
          {locales.map((code) => (
            <TextField
              key={code}
              lang={code}
              dir="auto"
              label={t('admin.media.alt', { language: t(`admin.language.${code}`) })}
              hint={t('admin.media.altHint')}
              maxLength={250}
              value={alt[code] ?? ''}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setAlt((current) => ({ ...current, [code]: value }));
              }}
            />
          ))}
          <div className="admin-actions">
            <Button type="submit" busy={busy}>
              {t('admin.save')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setEditing(false);
              }}
            >
              {t('admin.media.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
      {canManage && !editing ? (
        <div className="admin-actions">
          <Button
            variant="secondary"
            aria-label={t('admin.media.editFor', { name: item.filename })}
            onClick={startEditing}
          >
            {t('admin.media.edit')}
          </Button>
          {confirming ? (
            <>
              <Button variant="danger" busy={busy} onClick={() => void remove()}>
                {t('admin.media.confirmDelete')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {t('admin.media.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              aria-label={t('admin.media.deleteFor', { name: item.filename })}
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('admin.media.delete')}
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}
